import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as xpService from '@/services/xp.service';
import type {
  UserXPStats,
  XPTransaction,
  XPBreakdown,
  LevelsResponse,
} from '@/services/xp.service';
import { ApiError } from '@/services/api';

// State interface
interface XPState {
  stats: UserXPStats | null;
  history: XPTransaction[];
  breakdown: XPBreakdown[];
  levelsInfo: LevelsResponse | null;
  isLoadingStats: boolean;
  isLoadingHistory: boolean;
  isLoadingBreakdown: boolean;
  isLoadingLevels: boolean;
  error: string | null;
}

// Initial state
const initialState: XPState = {
  stats: null,
  history: [],
  breakdown: [],
  levelsInfo: null,
  isLoadingStats: false,
  isLoadingHistory: false,
  isLoadingBreakdown: false,
  isLoadingLevels: false,
  error: null,
};

// Thunks
export const fetchMyXPStats = createAsyncThunk<
  UserXPStats,
  void,
  { rejectValue: string }
>('xp/fetchMyStats', async (_, { rejectWithValue }) => {
  try {
    return await xpService.getMyXPStats();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der XP-Statistiken');
  }
});

export const fetchUserXPStats = createAsyncThunk<
  UserXPStats,
  string,
  { rejectValue: string }
>('xp/fetchUserStats', async (userId, { rejectWithValue }) => {
  try {
    return await xpService.getUserXPStats(userId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der XP-Statistiken');
  }
});

export const fetchXPHistory = createAsyncThunk<
  XPTransaction[],
  number | undefined,
  { rejectValue: string }
>('xp/fetchHistory', async (limit, { rejectWithValue }) => {
  try {
    return await xpService.getXPHistory(limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der XP-Historie');
  }
});

export const fetchXPBreakdown = createAsyncThunk<
  XPBreakdown[],
  void,
  { rejectValue: string }
>('xp/fetchBreakdown', async (_, { rejectWithValue }) => {
  try {
    return await xpService.getXPBreakdown();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der XP-Aufschluesselung');
  }
});

export const fetchLevelsInfo = createAsyncThunk<
  LevelsResponse,
  void,
  { rejectValue: string }
>('xp/fetchLevelsInfo', async (_, { rejectWithValue }) => {
  try {
    return await xpService.getLevelsInfo();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Level-Informationen');
  }
});

// Slice
const xpSlice = createSlice({
  name: 'xp',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearXPData: (state) => {
      state.stats = null;
      state.history = [];
      state.breakdown = [];
      state.levelsInfo = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch my XP stats
    builder
      .addCase(fetchMyXPStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchMyXPStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchMyXPStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch user XP stats
    builder
      .addCase(fetchUserXPStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchUserXPStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchUserXPStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch XP history
    builder
      .addCase(fetchXPHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchXPHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        state.history = action.payload;
      })
      .addCase(fetchXPHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch XP breakdown
    builder
      .addCase(fetchXPBreakdown.pending, (state) => {
        state.isLoadingBreakdown = true;
        state.error = null;
      })
      .addCase(fetchXPBreakdown.fulfilled, (state, action) => {
        state.isLoadingBreakdown = false;
        state.breakdown = action.payload;
      })
      .addCase(fetchXPBreakdown.rejected, (state, action) => {
        state.isLoadingBreakdown = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch levels info
    builder
      .addCase(fetchLevelsInfo.pending, (state) => {
        state.isLoadingLevels = true;
        state.error = null;
      })
      .addCase(fetchLevelsInfo.fulfilled, (state, action) => {
        state.isLoadingLevels = false;
        state.levelsInfo = action.payload;
      })
      .addCase(fetchLevelsInfo.rejected, (state, action) => {
        state.isLoadingLevels = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });
  },
});

export const { clearError, clearXPData } = xpSlice.actions;

export default xpSlice.reducer;
